import { AsyncThunkConfigWithRejectValue, createAsyncThunk } from '@/redux/createAsyncThunk';
import { fetchItemFacetsIfNeeded, getItemFacets } from '../../itemFacets';
import { fetchItemSummaryIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { fetchSavedItemCountsIfNeeded } from '@/redux/modules/savedItemCount';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';
import { getAuthToken, getBidderId } from '@/redux/modules/account/user/user.selectors';
import { getDeployment } from '@/redux/modules/config';
import { getFullUrl } from '@/utils/amplitude/analytics/utils/getFullUrl';
import {
    getItemSummaryCatalogId,
    getItemSummaryIsAvailable,
    getItemSummaryItemId,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import {
    getSavedItems,
    postSaveItem,
    postUnsaveItem,
    SavedItemQueryType,
} from '@/redux/modules/item/saved/savedItems.api';
import { getSaveItemAnalytics, trackConversionForAlgolia } from '@/redux/modules/analytics';
import { isItemSaved } from '@/redux/modules/item/saved/savedItems.selectors';
import { ItemSummary } from '@/types/item/ItemSummary';
import { Location } from 'react-router-dom';
import { MyFavoritesItemStatusTab } from '@/pages/MyItems/MyFavorites/types/MyFavoritesPage.types';
import { trackItemUnsavedAnalytics } from '@/utils/amplitude/analytics';

type SubmitSaveItemParams = {
    canHaveEasyShippingTag: boolean;
    catalogId?: number;
    itemId: number;
    location: Location;
};

export type ToggleSaveItemResponse = {
    isAvailable: boolean;
    itemId: number;
};

export const submitSaveItem = createAsyncThunk<
    ToggleSaveItemResponse,
    SubmitSaveItemParams,
    AsyncThunkConfigWithRejectValue
>(
    'la/ui/saveItem/submitSaveItem',
    async ({ catalogId: providedCatalogId, itemId, location }, { dispatch, getState, rejectWithValue }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        const storedItemId = getItemSummaryItemId(state, itemId);
        const storedCatalogId = getItemSummaryCatalogId(state, itemId);

        try {
            await postSaveItem({
                authToken,
                bidderId,
                catalogId: providedCatalogId ?? storedCatalogId,
                deployment,
                itemId,
            });

            if (!Boolean(storedItemId)) {
                await dispatch(
                    fetchItemSummaryIfNeeded({
                        identifier: 'save-item',
                        itemId,
                    })
                );
            }

            if (!Boolean(getItemFacets(state, itemId).categories)) {
                await dispatch(fetchItemFacetsIfNeeded([itemId]));
            }

            const newState = getState();
            dispatch(trackConversionForAlgolia(itemId));

            await dispatch(fetchSavedItemCountsIfNeeded([itemId], true));

            // Use newState to ensure we get the most up-to-date facets data
            getSaveItemAnalytics(newState, itemId, location);
            const isAvailable = getItemSummaryIsAvailable(newState, itemId);

            return { isAvailable, itemId };
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const submitUnsaveItem = createAsyncThunk<ToggleSaveItemResponse, number, AsyncThunkConfigWithRejectValue>(
    'la/ui/saveItem/submitUnsaveItem',
    async (itemId, { dispatch, getState, rejectWithValue }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        const storedItemId = getItemSummaryItemId(state, itemId);

        try {
            await postUnsaveItem({
                authToken,
                bidderId,
                deployment,
                itemId,
            });
            if (!Boolean(storedItemId)) {
                await dispatch(
                    fetchItemSummaryIfNeeded({
                        identifier: 'saved-item',
                        itemId,
                    })
                );
            }
            await dispatch(fetchSavedItemCountsIfNeeded([itemId], true));

            trackItemUnsavedAnalytics({
                pageName: getAmplitudePageNameFromPath(window.location.pathname),
                url: getFullUrl(deployment, window.location.pathname),
            });

            return {
                isAvailable: false,
                itemId,
            };
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export type FetchSavedItemsResponse = {
    items: ItemSummary[];
    tab: MyFavoritesItemStatusTab;
    totalRecords: number;
};

export const fetchSavedItems = createAsyncThunk<
    FetchSavedItemsResponse,
    SavedItemQueryType,
    AsyncThunkConfigWithRejectValue
>('la/ui/saveItem/fetchSavedItems', async (query, { getState, rejectWithValue }) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const deployment = getDeployment(state);
    const bidderId = getBidderId(state);

    try {
        const { payload } = await getSavedItems({
            authToken,
            bidderId,
            deployment,
            query,
        });
        return {
            items: payload.items,
            tab: query.status,
            totalRecords: payload.totalRecords,
        };
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const toggleItemSave = createAsyncThunk<void, SubmitSaveItemParams>(
    'la/ui/saveItem/toggleItemSave',
    async ({ itemId, ...rest }, { dispatch, getState }) => {
        if (isItemSaved(getState(), itemId)) {
            await dispatch(submitUnsaveItem(itemId));
        } else {
            await dispatch(
                submitSaveItem({
                    itemId,
                    ...rest,
                })
            );
        }
    }
);
